import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import SEO from '../components/SEO';
const Wrap = styled.div`
  width: min(100%, 1800px);
  margin: clamp(12px, 3vw, 28px) auto;
  background: rgba(255, 255, 255, 0.95);
  border-radius: clamp(50px, 3vw, 22px);
  padding: clamp(25px, 2.6vw, 24px);
  box-shadow: 0 4px 6px rgba(241, 134, 13, 0.79);
  overflow: hidden;
`;

const Header = styled.header`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding-bottom: 14px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);

  @media (max-width: 560px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const TitleBlock = styled.div`
  min-width: 0;
`;

const H1 = styled.h1`
  margin: 0;
  font-size: clamp(22px, 2.6vw, 30px);
  line-height: 1.15;
  color: #053969;
`;

const Sub = styled.p`
  margin: 8px 0 0;
  color: rgba(0, 0, 0, 0.65);
  font-size: clamp(12.5px, 1.2vw, 14px);
  line-height: 1.45;
`;

const LangRow = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  justify-content: flex-end;

  @media (max-width: 560px) {
    justify-content: flex-start;
  }
`;

const LangBtn = styled.button`
  border: 1px solid rgba(5, 57, 105, 0.22);
  background: ${(p) => (p.$active ? '#053969' : 'transparent')};
  color: ${(p) => (p.$active ? '#fff' : '#053969')};
  border-radius: 999px;
  padding: 8px 12px;
  font-weight: 800;
  cursor: pointer;
  transition: 0.15s ease;
  font-size: 13px;

  &:hover {
    transform: translateY(-1px);
    opacity: 0.98;
  }

  &:active {
    transform: translateY(0px);
  }
`;

const Content = styled.div`
  padding-top: clamp(14px, 2.2vw, 20px);
`;

const Toc = styled.nav`
  background: rgba(5, 57, 105, 0.05);
  border: 1px solid rgba(5, 57, 105, 0.1);
  border-radius: 16px;
  padding: 12px 12px;
  margin-bottom: 16px;
`;

const TocTitle = styled.div`
  font-weight: 900;
  color: #053969;
  margin-bottom: 10px;
  font-size: 14px;
`;

const TocList = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px 10px;

  @media (max-width: 560px) {
    grid-template-columns: 1fr;
  }
`;

const TocLink = styled.a`
  display: block;
  text-decoration: none;
  color: rgba(0, 0, 0, 0.78);
  padding: 8px 10px;
  border-radius: 12px;
  font-size: 13px;
  border: 1px solid rgba(0, 0, 0, 0.06);
  background: rgba(255, 255, 255, 0.7);

  &:hover {
    color: #053969;
    border-color: rgba(5, 57, 105, 0.2);
  }
`;

const Section = styled.section`
  padding: 14px 0;
  border-top: 1px solid rgba(0, 0, 0, 0.08);

  &:first-of-type {
    border-top: none;
    padding-top: 0;
  }
`;

const H2 = styled.h2`
  margin: 0 0 10px;
  color: #111;
  font-size: clamp(16px, 1.9vw, 20px);
  line-height: 1.2;
`;

const P = styled.p`
  margin: 0 0 10px;
  color: rgba(0, 0, 0, 0.78);
  font-size: clamp(13px, 1.25vw, 14.5px);
  line-height: 1.6;
`;

const Ul = styled.ul`
  margin: 0;
  padding-left: 18px;
  color: rgba(0, 0, 0, 0.78);
  font-size: clamp(13px, 1.25vw, 14.5px);
  line-height: 1.6;

  li {
    margin: 6px 0;
  }
`;

const Note = styled.div`
  margin-top: 14px;
  padding: 12px 14px;
  border-radius: 16px;
  background: rgba(0, 0, 0, 0.04);
  border: 1px solid rgba(0, 0, 0, 0.06);
  color: rgba(0, 0, 0, 0.7);
  font-size: 13px;
  line-height: 1.55;
`;

const Email = styled.a`
  color: #053969;
  font-weight: 800;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

export default function Terms() {
  const [lang, setLang] = useState('en');
  const contactEmail = 'alexandru.moloman94@gmail.com';
  const lastUpdated = '2026-01-01';

  const copy = useMemo(() => {
    const base = {
      tocTitle: 'On this page',
      subtitle: `Last updated: ${lastUpdated}`,
      sections: [
        {
          id: 'intro',
          title: '1. Introduction',
          body: [
            'These Terms of Service govern your use of this website and any services provided by the Developer. By using this website, you agree to these Terms.',
            'This website is operated by a single independent developer (“Developer”).',
          ],
        },
        {
          id: 'services',
          title: '2. Services',
          body: [
            'The Developer may provide website design and development services, consultation, and access to website templates and demo versions.',
            'Information shown on this website is for general purposes and may be updated at any time.',
          ],
        },
        {
          id: 'process',
          title: '3. Project Process',
          body: [
            'Project scope, timeline, and deliverables are agreed before work begins. Any new requirements requested during development may affect timeline and cost.',
            'The Client is responsible for providing required content (texts, images, branding) in a timely manner.',
          ],
        },
        {
          id: 'payments',
          title: '4. Payments',
          body: [
            'Payment terms are agreed individually per project. In most cases, work starts after an initial payment is received.',
            'If work has already started, the initial payment is generally non-refundable, unless otherwise agreed in writing.',
          ],
        },
        {
          id: 'revisions',
          title: '5. Revisions and Changes',
          body: [
            'Reasonable revisions are included as agreed for each project. Additional revisions or changes outside the agreed scope may require extra time and cost.',
            'A change is considered any request that alters agreed structure, features, or content beyond initial scope.',
          ],
        },
        {
          id: 'demo',
          title: '6. Demo Access and Templates',
          body: [
            'Demo credentials (login/password) are provided strictly for testing and evaluation purposes.',
            'Users must not misuse demo access, attempt to gain unauthorized control, modify data, or disrupt services.',
          ],
        },
        {
          id: 'ip',
          title: '7. Intellectual Property',
          body: [
            'Unless otherwise agreed, the Client receives rights to the final website deliverables after full payment.',
            'The Developer may showcase completed work in a portfolio unless the Client requests otherwise in writing.',
          ],
        },
        {
          id: 'thirdparty',
          title: '8. Third-Party Services',
          body: [
            'The website or projects may rely on third-party services such as hosting providers, domains, payment platforms, or analytics tools.',
            'The Developer is not responsible for outages, policy changes, or failures of third-party services.',
          ],
        },
        {
          id: 'liability',
          title: '9. Limitation of Liability',
          body: [
            'The Developer is not liable for indirect damages, loss of profits, business interruption, or losses arising from the use of the website or delivered services.',
            'No guarantees are made regarding business results (e.g., sales, SEO rankings, revenue).',
          ],
        },
        {
          id: 'termination',
          title: '10. Termination',
          body: [
            'The Developer may suspend or terminate cooperation if the Client violates these Terms, fails to provide required materials, or fails to pay as agreed.',
            'The Client may stop cooperation at any time, subject to payment for work already completed.',
          ],
        },
        {
          id: 'changes',
          title: '11. Changes and Contact',
          body: [
            'The Developer may update these Terms at any time. The most recent version will be available on this page.',
            'For any questions regarding these Terms, contact:',
          ],
          contact: true,
        },
      ],
      note: 'Note: demo credentials (login/password) are provided strictly for testing and evaluating templates. Please do not misuse them.',
    };

    const ru = {
      tocTitle: 'Содержание',
      subtitle: `Последнее обновление: ${lastUpdated}`,
      sections: [
        {
          id: 'intro',
          title: '1. Введение',
          body: [
            'Настоящие Условия использования регулируют использование этого сайта и услуг Разработчика. Используя сайт, вы соглашаетесь с этими условиями.',
            'Сайт управляется одним независимым разработчиком («Разработчик»).',
          ],
        },
        {
          id: 'services',
          title: '2. Услуги',
          body: [
            'Разработчик может предоставлять услуги по дизайну и разработке сайтов, консультации, а также доступ к шаблонам и демо-версиям.',
            'Информация на сайте носит общий характер и может обновляться в любое время.',
          ],
        },
        {
          id: 'process',
          title: '3. Процесс работы',
          body: [
            'Объём работ, сроки и результат согласуются до начала. Новые требования в процессе разработки могут повлиять на сроки и стоимость.',
            'Клиент отвечает за своевременное предоставление контента (тексты, изображения, бренд-материалы).',
          ],
        },
        {
          id: 'payments',
          title: '4. Оплата',
          body: [
            'Условия оплаты согласуются индивидуально для каждого проекта. Обычно работа начинается после получения предоплаты.',
            'Если работа уже началась, предоплата, как правило, не возвращается, если иное не согласовано письменно.',
          ],
        },
        {
          id: 'revisions',
          title: '5. Правки и изменения',
          body: [
            'Разумное количество правок включается согласно договорённостям по проекту. Дополнительные правки вне согласованного объёма могут требовать доп. времени и оплаты.',
            'Изменением считается запрос, который меняет согласованную структуру, функционал или контент сверх исходного объёма.',
          ],
        },
        {
          id: 'demo',
          title: '6. Демо-доступ и шаблоны',
          body: [
            'Демо-доступы (логин/пароль) предоставляются исключительно для тестирования и ознакомления.',
            'Запрещено неправомерное использование демо-доступа, попытки получить несанкционированный контроль, изменять данные или нарушать работу сервисов.',
          ],
        },
        {
          id: 'ip',
          title: '7. Интеллектуальная собственность',
          body: [
            'Если не согласовано иное, клиент получает права на итоговый результат после полной оплаты.',
            'Разработчик может использовать выполненные работы в портфолио, если клиент письменно не попросил об обратном.',
          ],
        },
        {
          id: 'thirdparty',
          title: '8. Сторонние сервисы',
          body: [
            'Сайт или проекты могут использовать сторонние сервисы (хостинг, домены, платёжные платформы, аналитика и т.д.).',
            'Разработчик не несёт ответственности за сбои, изменения правил или неисправности сторонних сервисов.',
          ],
        },
        {
          id: 'liability',
          title: '9. Ограничение ответственности',
          body: [
            'Разработчик не несёт ответственности за косвенный ущерб, упущенную выгоду, простой бизнеса или убытки, связанные с использованием сайта или услуг.',
            'Не даются гарантии бизнес-результатов (например, продаж, SEO-позиций, прибыли).',
          ],
        },
        {
          id: 'termination',
          title: '10. Прекращение сотрудничества',
          body: [
            'Разработчик может приостановить или прекратить сотрудничество при нарушении условий, отсутствии материалов от клиента или неоплате согласно договорённости.',
            'Клиент может прекратить сотрудничество в любое время, при этом оплачивается уже выполненная работа.',
          ],
        },
        {
          id: 'changes',
          title: '11. Изменения и контакты',
          body: [
            'Разработчик может обновлять эти условия в любое время. Актуальная версия всегда доступна на этой странице.',
            'По вопросам, связанным с условиями, обращайтесь:',
          ],
          contact: true,
        },
      ],
      note: 'Примечание: демонстрационные доступы (логин/пароль) предоставляются только для ознакомления и тестирования шаблонов. Пожалуйста, не используйте их для неправомерных действий.',
    };

    const it = {
      tocTitle: 'Indice',
      subtitle: `Ultimo aggiornamento: ${lastUpdated}`,
      sections: [
        {
          id: 'intro',
          title: '1. Introduzione',
          body: [
            'Questi Termini di Servizio regolano l’uso di questo sito e dei servizi del Sviluppatore. Utilizzando il sito, accetti questi Termini.',
            'Il sito è gestito da un singolo sviluppatore indipendente (“Sviluppatore”).',
          ],
        },
        {
          id: 'services',
          title: '2. Servizi',
          body: [
            'Lo Sviluppatore può offrire servizi di design e sviluppo web, consulenze e accesso a template e demo.',
            'Le informazioni presenti sul sito sono generali e possono essere aggiornate in qualsiasi momento.',
          ],
        },
        {
          id: 'process',
          title: '3. Processo di lavoro',
          body: [
            'Ambito, tempi e deliverable vengono concordati prima dell’inizio. Nuove richieste durante lo sviluppo possono incidere su tempi e costi.',
            'Il Cliente è responsabile di fornire contenuti (testi, immagini, branding) in modo tempestivo.',
          ],
        },
        {
          id: 'payments',
          title: '4. Pagamenti',
          body: [
            'Le condizioni di pagamento sono concordate caso per caso. Di norma, il lavoro inizia dopo il pagamento iniziale.',
            'Se il lavoro è già iniziato, il pagamento iniziale normalmente non è rimborsabile, salvo accordi scritti diversi.',
          ],
        },
        {
          id: 'revisions',
          title: '5. Revisioni e modifiche',
          body: [
            'Le revisioni ragionevoli sono incluse secondo quanto concordato. Ulteriori modifiche fuori scope possono richiedere tempi e costi aggiuntivi.',
            'Una modifica è qualsiasi richiesta che altera struttura, funzionalità o contenuti oltre lo scope iniziale.',
          ],
        },
        {
          id: 'demo',
          title: '6. Accesso demo e template',
          body: [
            'Le credenziali demo (login/password) sono fornite esclusivamente per test e valutazione.',
            'È vietato un uso improprio dell’accesso demo, tentativi di controllo non autorizzato, modifica dei dati o interruzione dei servizi.',
          ],
        },
        {
          id: 'ip',
          title: '7. Proprietà intellettuale',
          body: [
            'Salvo accordi diversi, il Cliente riceve i diritti sui deliverable finali dopo il pagamento completo.',
            'Lo Sviluppatore può mostrare i lavori nel portfolio salvo richiesta scritta contraria del Cliente.',
          ],
        },
        {
          id: 'thirdparty',
          title: '8. Servizi di terze parti',
          body: [
            'Il sito o i progetti possono utilizzare servizi di terze parti (hosting, domini, pagamenti, analytics, ecc.).',
            'Lo Sviluppatore non è responsabile di disservizi, cambi di policy o malfunzionamenti di servizi di terze parti.',
          ],
        },
        {
          id: 'liability',
          title: '9. Limitazione di responsabilità',
          body: [
            'Lo Sviluppatore non è responsabile per danni indiretti, perdita di profitti, interruzioni di attività o perdite legate all’uso del sito o dei servizi.',
            'Non vengono garantiti risultati di business (vendite, posizionamenti SEO, ricavi).',
          ],
        },
        {
          id: 'termination',
          title: '10. Risoluzione',
          body: [
            'Lo Sviluppatore può sospendere o terminare la collaborazione in caso di violazione dei Termini, mancata consegna dei materiali o mancato pagamento.',
            'Il Cliente può interrompere la collaborazione in qualsiasi momento, con pagamento del lavoro già svolto.',
          ],
        },
        {
          id: 'changes',
          title: '11. Modifiche e contatti',
          body: [
            'Lo Sviluppatore può aggiornare questi Termini in qualsiasi momento. La versione più recente sarà disponibile su questa pagina.',
            'Per domande su questi Termini, contatta:',
          ],
          contact: true,
        },
      ],
      note: 'Nota: le credenziali demo (login/password) vengono fornite esclusivamente per test e valutazione dei template. Si prega di non utilizzarle in modo improprio.',
    };

    if (lang === 'ru') return ru;
    if (lang === 'it') return it;
    return base;
  }, [lang, lastUpdated]);

  const pageTitle =
    lang === 'ru'
      ? 'Условия использования'
      : lang === 'it'
      ? 'Termini di Servizio'
      : 'Terms of Service';

  return (
    <Wrap>
      <SEO
        title="Terms of Service"
        description="Terms and conditions for using website templates and services."
        path="/terms"
      />
      <Header>
        <TitleBlock>
          <H1>{pageTitle}</H1>
          <Sub>{copy.subtitle}</Sub>
        </TitleBlock>

        <LangRow>
          <LangBtn type="button" onClick={() => setLang('en')} $active={lang === 'en'}>
            EN
          </LangBtn>
          <LangBtn type="button" onClick={() => setLang('ru')} $active={lang === 'ru'}>
            RU
          </LangBtn>
          <LangBtn type="button" onClick={() => setLang('it')} $active={lang === 'it'}>
            IT
          </LangBtn>
        </LangRow>
      </Header>

      <Content>
        <Toc aria-label="Table of contents">
          <TocTitle>{copy.tocTitle}</TocTitle>
          <TocList>
            {copy.sections.map((s) => (
              <TocLink key={s.id} href={`#${s.id}`}>
                {s.title}
              </TocLink>
            ))}
          </TocList>
        </Toc>

        {copy.sections.map((s) => (
          <Section key={s.id} id={s.id}>
            <H2>{s.title}</H2>

            {s.body?.map((text, idx) => (
              <P key={idx}>{text}</P>
            ))}

            {s.list?.length ? (
              <Ul>
                {s.list.map((li, idx) => (
                  <li key={idx}>{li}</li>
                ))}
              </Ul>
            ) : null}

            {s.contact ? (
              <P>
                <Email href={`mailto:${contactEmail}`}>{contactEmail}</Email>
              </P>
            ) : null}
          </Section>
        ))}

        <Note>{copy.note}</Note>
      </Content>
    </Wrap>
  );
}
